import { useState, useEffect } from 'react';
import { LibraryDB } from '../services/LibraryDB';
import { DownloadConfirmModal } from './DownloadConfirmModal';
import { downloadManager } from '../lib/DownloadManager';
import { GutendexService } from '../services/Gutendex';

interface Props {
    onOpenBook: (id: string) => void;
    manifest: any[];
    onGoToLibrary?: () => void;
    onPlayAudio?: (id: string) => void;
    onToast?: (msg: string) => void;
    [key: string]: any;
}

export const DiscoverView = ({ onOpenBook, manifest = [] }: Props) => {
    // Image Fallback Handler (Same as LibraryView)
    const handleImageError = (e: any, book: any) => {
        const img = e.target as HTMLImageElement;
        const currentSrc = img.src;
        // Fallback chain: grid -> full -> hi -> master -> placeholder
        if (currentSrc.includes('cover_grid.webp')) {
            img.src = currentSrc.replace('cover_grid.webp', 'cover_full.webp');
        } else if (currentSrc.includes('cover_full.webp')) {
            img.src = currentSrc.replace('cover_full.webp', 'cover_hi.webp');
        } else if (currentSrc.includes('cover_hi.webp')) {
            img.src = currentSrc.replace('cover_hi.webp', 'cover.webp');
        } else {
            img.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg"/>';
            img.setAttribute('data-failed', 'true');
            img.onerror = null;
        }
    };

    const [search, setSearch] = useState('');
    const [results, setResults] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [libraryStatus, setLibraryStatus] = useState<Record<string, string>>({});
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    // Modal State
    const [modalBook, setModalBook] = useState<any | null>(null);

    // Load Library Status
    useEffect(() => {
        LibraryDB.getAllBooks().then(books => {
            const statusMap: Record<string, string> = {};
            books.forEach(b => {
                statusMap[b.id] = b.status || 'ready';
            });
            setLibraryStatus(statusMap);
        });
    }, [refreshTrigger, search, results]);

    // Ensure solid background to prevent layering text artifacts
    const containerStyle = {
        background: 'var(--bg-color)',
        height: 'calc(100vh - 50px)',
        overflowY: 'auto' as const,
        padding: '20px'
    };

    // Initial Load (Book Club) or Search
    useEffect(() => {
        // Safe access to manifest
        const safeManifest = Array.isArray(manifest) ? manifest : [];
        if (!search && safeManifest.length > 0) {
            // Show Book Club items by default
            // FILTER: Exclude if trackCount is explicitly 0 (no audio)
            setResults(safeManifest
                .filter(item => item.trackCount !== 0)
                .map(item => normalizeManifest(item)));
            return;
        }

        if (!search) {
            setResults([]);
            return;
        }

        setLoading(true);
        const q = search;

        // 1. Search Gutendex
        const url = `https://gutendex.com/books/?search=${encodeURIComponent(q)}`;
        fetch(url).then(r => r.json()).then(d => {
            let gutendexResults: any[] = [];
            if (d.results) {
                gutendexResults = d.results.map((b: any) => normalizeGutenberg(b));
            }

            // 2. Search Local Manifest
            const lowerQ = q.toLowerCase();
            const localResults = manifest
                .filter(m => (m.title.toLowerCase().includes(lowerQ) || m.author.toLowerCase().includes(lowerQ)) && m.trackCount !== 0)
                .map(m => normalizeManifest(m));

            // 3. Merge (Local first)
            // Deduplicate by ID if needed, but IDs are effectively namespaced
            setResults([...localResults, ...gutendexResults]);
            setLoading(false);
        }).catch(e => {
            console.error(e);
            setLoading(false);
        });

    }, [search, manifest]);

    const normalizeManifest = (item: any) => ({
        id: item.id,
        title: item.title,
        authors: [{ name: item.author }],
        coverUrl: item.coverUrl || (item.id === 'frankenstein' ? '/covers/frankenstein.jpg' : `/api/library/cover/${item.id}`),
        isBookClub: true,
        ts: item.ts,
        languages: ['en']
    });

    const normalizeGutenberg = (item: any) => ({
        id: `gutenberg-${item.id}`,
        gutenbergId: item.id,
        title: item.title,
        authors: item.authors,
        coverUrl: item.formats['image/jpeg'],
        isBookClub: false,
        languages: item.languages
    });

    const handleSaveClick = (book: any) => {
        if (book.isBookClub) {
            setModalBook(book); // Trigger Modal
        } else {
            // Auto-save Public Domain (Text only usually)
            saveBookToLibrary(book, false, false);
        }
    };

    const saveBookToLibrary = async (book: any, isBookClub: boolean, downloadAudio: boolean) => {
        const entry = {
            id: book.id,
            title: book.title,
            authors: book.authors,
            languages: book.languages,
            coverUrl: book.coverUrl,
            formats: {},
            addedAt: Date.now(),
            status: 'ready' as const, // Metadata ready
            isBookClub: isBookClub
        };

        await LibraryDB.addBook(entry);
        setRefreshTrigger(p => p + 1); // Update UI

        if (downloadAudio && isBookClub) {
            // Trigger Download Manager
            downloadManager.startDownload(book.id, `/api/library/playlist/${book.id}`);
        }

        setModalBook(null);
    };

    return (
        <div style={containerStyle}>
            <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
                <input
                    autoFocus
                    type="text"
                    placeholder="Search Gutenberg & Library..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    style={{
                        flex: 1, padding: '12px', borderRadius: '8px',
                        border: '1px solid var(--border-color)',
                        background: 'var(--surface-color)', color: 'var(--text-color)'
                    }}
                />
            </div>

            {loading && <div style={{ textAlign: 'center', padding: '20px' }}>Searching...</div>}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '20px', paddingBottom: '40px' }}>
                {results.map(book => {
                    const inLibrary = !!libraryStatus[book.id];

                    return (
                        <div key={book.id} style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <div
                                onClick={() => onOpenBook(book.id)}
                                style={{
                                    aspectRatio: '2/3', background: '#333', borderRadius: '8px',
                                    overflow: 'hidden', position: 'relative', cursor: 'pointer',
                                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                                }}
                            >
                                {book.coverUrl ? (
                                    <img
                                        src={book.coverUrl ? `${book.coverUrl}${book.isBookClub ? '?variant=grid' : ''}&v=${book.ts || 0}` : ''}
                                        alt={book.title}
                                        loading="lazy"
                                        decoding="async"
                                        width="140"
                                        height="210"
                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                        onError={(e) => handleImageError(e, book)}
                                    />
                                ) : (
                                    <div style={{ padding: '10px', color: '#888', display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                                        No Cover
                                    </div>
                                )}

                                {book.isBookClub && (
                                    <div style={{
                                        position: 'absolute', top: 5, right: 5,
                                        background: 'gold', color: 'black',
                                        fontSize: '0.7em', padding: '2px 5px', borderRadius: '4px', fontWeight: 'bold'
                                    }}>
                                        CLUB
                                    </div>
                                )}

                                {inLibrary && (
                                    <div style={{
                                        position: 'absolute', top: 5, left: 5,
                                        background: '#4CAF50', color: 'white',
                                        fontSize: '0.7em', padding: '2px 5px', borderRadius: '4px', fontWeight: 'bold'
                                    }}>
                                        SAVED
                                    </div>
                                )}
                            </div>

                            <div style={{ fontSize: '0.95em', fontWeight: 600, lineHeight: 1.2, height: '2.4em', overflow: 'hidden' }}>
                                {book.title}
                            </div>
                            <div style={{ fontSize: '0.85em', color: 'var(--text-secondary)' }}>
                                {book.authors?.[0]?.name || 'Unknown'}
                            </div>

                            {!inLibrary ? (
                                <button
                                    onClick={(e) => { e.stopPropagation(); handleSaveClick(book); }}
                                    style={{
                                        padding: '8px', borderRadius: '6px', border: '1px solid var(--border-color)',
                                        background: 'var(--surface-color)', color: 'var(--text-color)',
                                        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px',
                                        fontSize: '0.9em'
                                    }}
                                >
                                    <span>📌</span> Save
                                </button>
                            ) : (
                                <button disabled style={{
                                    padding: '8px', borderRadius: '6px', border: 'none',
                                    background: 'transparent', color: 'var(--text-secondary)',
                                    fontSize: '0.9em', cursor: 'default'
                                }}>
                                    Saved ✓
                                </button>
                            )}
                        </div>
                    );
                })}
            </div>

            {modalBook && (
                <DownloadConfirmModal
                    title={modalBook.title}
                    onClose={() => setModalBook(null)}
                    onSaveOnly={() => saveBookToLibrary(modalBook, true, false)}
                    onSaveAndDownload={() => saveBookToLibrary(modalBook, true, true)}
                />
            )}
        </div>
    );
};
